import { GraphConfiguration } from "@/type/common";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import "./table-item.scss";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { cleanDomain } from "@/utils/refactor";
import { useTranslation } from "react-i18next";

const SkeletonRow = () => {
  return (
    <div className="flex flex-row items-center justify-between w-full gap-2">
      <Skeleton className="w-1/6 h-6" />
      <Skeleton className="w-1/6 h-6" />
      <Skeleton className="w-1/6 h-6" />
      <Skeleton className="w-1/6 h-6" />
      <Skeleton className="w-1/6 h-6" />
      <Skeleton className="w-1/6 h-6" />
    </div>
  );
};

const TableItem = ({
  config,
  data,
  models,
}: {
  config: GraphConfiguration;
  data: Record<string, any>[];
  models: any[];
}) => {
  const { t } = useTranslation();
  // Format cell value based on column configuration
  const formatCellValue = (value: any, columnConfig: any) => {
    if (value === undefined || value === null) {
      return "-";
    }

    if (typeof value === "object") {
      return value.name || JSON.stringify(value);
    }

    let formattedValue = value;

    // Format based on column format if specified
    if (columnConfig.format && typeof value === "number") {
      const decimals = columnConfig.decimals ?? 2;
      
      switch (columnConfig.format) {
        case "number":
          formattedValue = value.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
          break;
        case "decimal":
          formattedValue = value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
          break;
        case "percent":
          formattedValue = `${(value * 100).toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })}%`;
          break;
        case "currency":
          formattedValue = value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
          break;
        case "compact":
          if (value >= 1000000) {
            formattedValue = `${(value / 1000000).toLocaleString(undefined, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })}M`;
          } else if (value >= 1000) {
            formattedValue = `${(value / 1000).toLocaleString(undefined, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })}K`;
          } else {
            formattedValue = value.toLocaleString(undefined, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            });
          }
          break;
        default:
          formattedValue = value;
      }
    }

    // Apply prefix and suffix
    const prefix = columnConfig.prefix || "";
    const suffix = columnConfig.suffix || "";
    
    return `${prefix}${formattedValue}${suffix}`;
  };

  // Check if we have actual data to display
  const hasData = Array.isArray(data) && data.length > 0;
  // Get table columns from the configuration
  let columns = config.table_options?.columns || [];

  if (
    data.length !== 0 &&
    data[0].key &&
    config.data_source.groupBy.length > 0
  ) {
    const field = config.data_source.groupBy[0];
    // Use field label from enriched groupBy object, otherwise fall back to field name
    const fieldLabel = (field as any).label || field.field;
    columns = [
      {
        align: "left",
        field: "key",
        total: "sum",
        width: 120,
        header: fieldLabel,
        sortable: true,
        aggregation: "count",
      },
      ...columns,
    ];
  }

  return (
    <Card className="gap-2 h-full w-full pb-2 overflow-hidden">
      <CardHeader>
        <CardTitle>
          {config.title && config.title !== ""
            ? config.title
            : t("editor.graphConfig.data.unNamedTable")}
        </CardTitle>
      </CardHeader>
      <CardContent className="h-full px-0">
        {hasData ? (
          <ScrollArea className="w-full h-[calc(100%-20px)]">
            <ScrollBar orientation="vertical" />
            <ScrollBar orientation="horizontal" />
            <Table className="classic-table">
              <TableHeader>
                <TableRow
                  className={
                    config.table_options?.styling?.highlightHover
                      ? "hover:bg-muted/50"
                      : "hover:bg-unset"
                  }
                >
                  {columns.map((column, index) => (
                    <TableHead
                      key={index}
                      className={
                        column.align === "right"
                          ? "text-right"
                          : column.align === "center"
                          ? "text-center"
                          : "text-left"
                      }
                      style={{
                        minWidth:
                          column.width > 0 ? `${column.width}px` : "auto",
                      }}
                    >
                      {column.header || column.field}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data
                  .slice(0, config.table_options?.pageSize || 10)
                  .map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      className={`${
                        config.table_options?.styling?.striped &&
                        rowIndex % 2 === 1
                          ? "bg-muted/50"
                          : ""
                      } ${
                        config.table_options?.styling?.highlightHover
                          ? "hover:bg-muted/50"
                          : "hover:bg-unset"
                      }`}
                      onClick={() => {
                        if (config.data_source.sqlRequest) {
                          toast.info(
                            "You can't navigate on a graph with a SQL request"
                          );
                          return;
                        }
                        if (window.location.href.includes("/edit")) {
                          toast.info(
                            "You can't navigate on a graph in edit mode"
                          );
                          return;
                        }
                        const target: any = {
                          model: config.model,
                          type: "navigate",
                          name: models.find((m) => m.value === config.model)
                            ?.label,
                        };

                        if (!row.key) {
                          target.res_id = row.id;
                        } else {
                          target.domain = config.data_source.domain
                            ? [...config.data_source.domain, ...row.__domain]
                            : row.__domain;
                        }

                        target.domain = cleanDomain(target.domain);

                        window.parent.postMessage(target, "*");
                      }}
                    >
                      {columns.map((column, colIndex) => (
                        <TableCell
                          key={colIndex}
                          className={
                            column.align === "right"
                              ? "text-right"
                              : column.align === "center"
                              ? "text-center"
                              : "text-left"
                          }
                        >
                          {formatCellValue(row[column.field], column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </ScrollArea>
        ) : (
          <div className="flex flex-col gap-2 p-4">
            <SkeletonRow />
            <div></div>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(TableItem);
